










































































import Vue from "vue";
import userManager from "@/services/userManager";
import coreApiClient from "@/services/apis/coreApiClient";
import CPORoaming from "./CPORoaming.vue"

export default Vue.extend({
  name: "ZnsIntegration",
  components: {
    CPORoaming
  },
  data() {
    return {
      hasPermission: false,
      callbackUrl: "",
      authUrl: "",
      authMessage: "",
      tokenStatus: null as { hasToken: boolean; message: string } | null,
      testPhone: "",
      otpCode: "",
      otpSent: false,
      loadingAuthUrl: false,
      loadingTokenStatus: false,
      loadingSendOtp: false,
      loadingVerifyOtp: false,
      loadingSettings: false,
      settings: {
        smsActiveRegisterEnabled: false,
        smsForgotPasswordEnabled: false,
      },
      config: {
        zaloAppId: "",
        zaloSecretKey: "",
        znsTemplateId: "",
      },
      originalConfig: {
        zaloAppId: "",
        zaloSecretKey: "",
        znsTemplateId: "",
      },
      configSaved: false,
      loadingConfig: false,
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
    };
  },
  computed: {
    isConfigValid() {
      const hasRequiredFields =
        this.config.zaloAppId.trim() !== "" &&
        this.config.znsTemplateId.trim() !== "";

      // If App ID or Template ID changed from original values, Secret Key is required
      const appIdChanged =
        this.config.zaloAppId !== this.originalConfig.zaloAppId;
      const templateIdChanged =
        this.config.znsTemplateId !== this.originalConfig.znsTemplateId;
      const fieldsChanged = appIdChanged || templateIdChanged;

      if (fieldsChanged && this.config.zaloSecretKey.trim() === "") {
        return false;
      }

      return hasRequiredFields;
    },
  },
  created() {
    this.checkPermission();
    this.loadCpos();
    this.loadConfig();
  },
  methods: {
    checkPermission() {
      this.hasPermission = userManager.checkRole(["systemAdmin"]);
    },

    copyToClipboard(text: string) {
      navigator.clipboard.writeText(text).then(() => {
        this.showSnackbar("Callback URL copied to clipboard!", "success");
      });
    },

    async getAuthUrl() {
      this.loadingAuthUrl = true;
      try {
        const response = await coreApiClient.callRaw("sms", "getAuthUrl");

        this.authUrl = response.authUrl;
        this.authMessage = response.message;
        this.showSnackbar(
          "Authorization URL generated successfully!",
          "success"
        );
      } catch (error) {
        this.showSnackbar(
          `Error: ${error.message || "Failed to get authorization URL"}`,
          "error"
        );
      } finally {
        this.loadingAuthUrl = false;
      }
    },

    openAuthUrl() {
      if (this.authUrl) {
        window.open(this.authUrl, "_blank");
      }
    },

    async checkTokenStatus() {
      this.loadingTokenStatus = true;
      try {
        const response = await coreApiClient.callRaw("sms", "getTokenStatus");

        this.tokenStatus = response;
        this.showSnackbar("Token status checked successfully!", "success");
      } catch (error) {
        this.showSnackbar(
          `Error: ${error.message || "Failed to check token status"}`,
          "error"
        );
      } finally {
        this.loadingTokenStatus = false;
      }
    },

    async loadCpos() {
      try {
        const response = await coreApiClient.call("ocpiServerCredentialsRoles", "findAllCPO");
        this.settings.smsActiveRegisterEnabled =
          response.smsActiveRegisterEnabled || false;
        this.settings.smsForgotPasswordEnabled =
          response.smsForgotPasswordEnabled || false;
      } catch (error) {
        this.showSnackbar("Failed to load settings", "error");
      }
    },

    async loadConfig() {
      try {
        const response = await coreApiClient.call("setting", "get");
        this.config.zaloAppId = response.zaloAppId || "";
        this.config.zaloSecretKey = response.zaloSecretKey || "";
        this.config.znsTemplateId = response.znsTemplateId || "";

        // Store original values for comparison
        this.originalConfig.zaloAppId = response.zaloAppId || "";
        this.originalConfig.zaloSecretKey = response.zaloSecretKey || "";
        this.originalConfig.znsTemplateId = response.znsTemplateId || "";

        // Check if configuration is already saved
        this.configSaved =
          this.isConfigValid &&
          (response.zaloAppId ||
            response.zaloSecretKey ||
            response.znsTemplateId);

        // Only check token status if config is saved
        if (this.configSaved) {
          this.checkTokenStatus();
        }
      } catch (error) {
        this.showSnackbar("Failed to load configuration", "error");
      }
    },

    async saveConfig() {
      if (!this.isConfigValid) {
        const appIdChanged =
          this.config.zaloAppId !== this.originalConfig.zaloAppId;
        const templateIdChanged =
          this.config.znsTemplateId !== this.originalConfig.znsTemplateId;

        if (
          (appIdChanged || templateIdChanged) &&
          this.config.zaloSecretKey.trim() === ""
        ) {
          this.showSnackbar(
            "Zalo Secret Key is required when App ID or Template ID is changed",
            "warning"
          );
        } else {
          this.showSnackbar(
            "Please fill in Zalo App ID and ZNS Template ID",
            "warning"
          );
        }
        return;
      }

      this.loadingConfig = true;
      try {
        const requestData: any = {
          data: {
            zaloAppId: this.config.zaloAppId,
            znsTemplateId: this.config.znsTemplateId,
          },
        };

        // Only include secret key if it's provided
        if (this.config.zaloSecretKey.trim() !== "") {
          requestData.data.zaloSecretKey = this.config.zaloSecretKey;
        }

        await coreApiClient.call("setting", "update", undefined, requestData);
        this.configSaved = true;
        this.showSnackbar("Configuration saved successfully!", "success");

        // Update original config values after successful save
        this.originalConfig.zaloAppId = this.config.zaloAppId;
        this.originalConfig.znsTemplateId = this.config.znsTemplateId;
        if (this.config.zaloSecretKey.trim() !== "") {
          this.originalConfig.zaloSecretKey = this.config.zaloSecretKey;
        }

        // Check token status after saving config
        this.checkTokenStatus();
      } catch (error) {
        this.showSnackbar(
          `Error saving configuration: ${error.message || "Unknown error"}`,
          "error"
        );
      } finally {
        this.loadingConfig = false;
      }
    },

    async updateSetting(key: string, value: boolean) {
      this.loadingSettings = true;
      try {
        const requestData = {
          data: {
            [key]: value,
          },
        };

        await coreApiClient.call("setting", "update", undefined, requestData);
        this.showSnackbar(`${key} updated successfully!`, "success");
      } catch (error) {
        this.showSnackbar(
          `Error updating ${key}: ${error.message || "Unknown error"}`,
          "error"
        );
        this.settings[key] = !value;
      } finally {
        this.loadingSettings = false;
      }
    },

    showSnackbar(message: string, color: string) {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
  },
});
